import React, { useState, useRef, useEffect, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { API_URL } from '../../utils/api';
import { LeafIcon } from '../ui/Icons';
import { Send, User, Loader2 } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const BotaniqChat = ({ plant }) => {
    const authContext = useContext(AuthContext);
    const authToken = authContext?.authToken;

    // Safety check for plant
    if (!plant) return <div className="p-4">Keine Pflanze ausgewählt.</div>;

    const [messages, setMessages] = useState([
        {
            role: 'assistant',
            content: `Hallo! Ich bin Botaniq. Was möchtest du über **${plant.german_name}** wissen?`
        }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const messagesEndRef = useRef(null);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    useEffect(() => {
        scrollToBottom();
    }, [messages]);

    const handleSend = async (e) => {
        e.preventDefault();
        if (!input.trim() || isLoading) return;

        const userMessage = input.trim();
        setInput('');

        // Add User Message
        const newHistory = [...messages, { role: 'user', content: userMessage }];
        setMessages(newHistory);
        setIsLoading(true);

        try {
            const response = await fetch(`${API_URL}/api/botaniq/chat/`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${authToken}`
                },
                body: JSON.stringify({
                    plant_id: plant.id,
                    question: userMessage,
                    history: newHistory.filter(m => m.role !== 'system')
                })
            });

            if (!response.ok) {
                const errText = await response.text();
                throw new Error(`Server returned ${response.status}: ${errText}`);
            }

            const data = await response.json();
            setMessages(prev => [...prev, { role: 'assistant', content: data.answer }]);

        } catch (error) {
            console.error("Botaniq Error:", error);
            setMessages(prev => [...prev, {
                role: 'assistant',
                content: `Entschuldigung, ich habe Verbindungsprobleme. (${error.message})`
            }]);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="flex flex-col h-full bg-gray-50 dark:bg-[#0a0a0a] rounded-xl overflow-hidden border border-gray-200 dark:border-gray-800">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-600 p-3 flex items-center gap-3 shadow-md z-10">
                <div className="w-8 h-8 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center border border-white/20">
                    <LeafIcon className="w-4 h-4 text-white" />
                </div>
                <div>
                    <h3 className="font-bold text-white text-sm leading-tight">Botaniq AI</h3>
                    <p className="text-[10px] text-blue-100 font-medium">Dein Pflanzen-Experte</p>
                </div>
            </div>

            {/* Chat Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4 custom-scrollbar">
                {messages.map((msg, index) => (
                    <div
                        key={index}
                        className={`flex gap-3 ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}
                    >
                        <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center border shadow-sm
                            ${msg.role === 'user'
                                ? 'bg-white dark:bg-[#1e1e1e] border-gray-200 dark:border-gray-700'
                                : 'bg-gradient-to-br from-blue-500 to-indigo-600 border-transparent'
                            }`}
                        >
                            {msg.role === 'user'
                                ? <User className="w-4 h-4 text-gray-600 dark:text-gray-300" />
                                : <LeafIcon className="w-4 h-4 text-white" />
                            }
                        </div>

                        <div className={`max-w-[85%] rounded-2xl px-4 py-3 text-sm shadow-sm
                            ${msg.role === 'user'
                                ? 'bg-white dark:bg-[#1e1e1e] text-gray-800 dark:text-gray-100 border border-gray-100 dark:border-gray-800 rounded-tr-none'
                                : 'bg-blue-50 dark:bg-blue-900/20 text-blue-900 dark:text-blue-100 border border-blue-100 dark:border-blue-900/30 rounded-tl-none'
                            }`}
                        >
                            <div className="prose dark:prose-invert prose-sm max-w-none">
                                <ReactMarkdown remarkPlugins={[remarkGfm]}>{msg.content}</ReactMarkdown>
                            </div>
                        </div>
                    </div>
                ))}

                {isLoading && (
                    <div className="flex gap-3">
                        <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center border-transparent shadow-sm">
                            <LeafIcon className="w-4 h-4 text-white" />
                        </div>
                        <div className="bg-blue-50 dark:bg-blue-900/20 px-4 py-3 rounded-2xl rounded-tl-none border border-blue-100 dark:border-blue-900/30 flex items-center gap-2">
                            <Loader2 className="w-4 h-4 text-blue-500 animate-spin" />
                            <span className="text-xs text-blue-500 font-medium">Botaniq denkt nach...</span>
                        </div>
                    </div>
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <form onSubmit={handleSend} className="p-3 bg-white dark:bg-[#1e1e1e] border-t border-gray-100 dark:border-gray-800 flex gap-2">
                <input
                    type="text"
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    placeholder="Frag etwas über diese Pflanze..."
                    className="flex-1 bg-gray-50 dark:bg-[#0a0a0a] border border-gray-200 dark:border-gray-800 rounded-xl px-4 py-3 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition-all dark:text-white"
                    disabled={isLoading}
                />
                <button
                    type="submit"
                    disabled={!input.trim() || isLoading}
                    className="w-12 h-12 flex items-center justify-center rounded-xl bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-lg shadow-blue-600/20"
                >
                    {isLoading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
                </button>
            </form>
        </div>
    );
};

export default BotaniqChat;
